import * as React from "react"
import type { DateRange, Matcher } from "react-day-picker"

import { cn } from "../../../lib/utils"
import { Button } from "../button/button"
import { Calendar } from "../calendar/calendar"
import { Icon } from "../icon/icon"
import { Popover, PopoverContent, PopoverTrigger } from "../popover/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../tooltip/tooltip"
import { Separator } from "../separator/separator"
import { DateRangePresetRail, type DateRangePreset } from "./date-range-preset-rail.js"
import { MonthRangePickerPanel } from "./month-range-picker-panel.js"
import {
  countMonths,
  formatMonthRangeLabel,
  snapRangeToMonths,
} from "./month-range.js"

export type { DateRange }
export type { DateRangePreset }

const NARROW_VIEWPORT_QUERY = "(max-width: 767px)"

/**
 * Gutter that matches the preset rail's `md:w-56`. The rail is taken out of
 * flow on desktop so the two-month calendar — not the 14-row catalog — sets
 * the popover height (hotel/flight picker). Keep these two classes in lockstep.
 */
const PRESET_RAIL_OFFSET_CLASS = "md:pl-56"
const PRESET_RAIL_LAYOUT_CLASS =
  "md:absolute md:inset-y-0 md:left-0 md:overflow-hidden"

/** Tracks the design system's `md` breakpoint so the popover can stack. */
function useIsNarrowViewport(): boolean {
  const [isNarrow, setIsNarrow] = React.useState(false)

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const query = window.matchMedia(NARROW_VIEWPORT_QUERY)
    setIsNarrow(query.matches)
    const onChange = (event: MediaQueryListEvent) => setIsNarrow(event.matches)
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [])

  return isNarrow
}

const DISPLAY_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
})

const MS_PER_DAY = 1000 * 60 * 60 * 24

function formatDay(date: Date): string {
  return DISPLAY_FORMAT.format(date)
}

function countNights(range: DateRange | undefined): number {
  if (!range?.from || !range.to) return 0
  return Math.round((range.to.getTime() - range.from.getTime()) / MS_PER_DAY)
}

export type DateRangePickerLabels = {
  /** Trigger placeholder when nothing is selected. */
  placeholder?: string
  /** Accessible label for the trigger button. */
  triggerAria?: string
  clear?: string
  done?: string
  apply?: string
  /** Renders the `{{count}}`-aware nights summary, e.g. "3 nights". */
  nights?: (count: number) => string
  /** Renders the `{{count}}`-aware months summary when `selectMonth` is enabled. */
  months?: (count: number) => string
  /** Labels for independent month pickers when `independentMonths` is enabled. */
  from?: string
  to?: string
  month?: string
  year?: string
  invalidRange?: string
  previousYear?: string
  nextYear?: string
  /** Status-row copy shown when the selection matches no preset. */
  custom?: string
  /** Accessible name for the preset rail's listbox. */
  presetList?: string
}

export type DateRangePickerProps = {
  value?: DateRange
  onChange: (range: DateRange | undefined) => void
  /** Preset rail options, with ranges already resolved by the consumer. */
  presets?: DateRangePreset[]
  activePresetId?: string
  onPresetSelect?: (id: string) => void
  /**
   * Rail mode only. Fires once on Apply with the whole draft, so the range and
   * the preset id land together — two callbacks could commit a preset that
   * disagrees with its dates.
   */
  onCommit?: (next: { range: DateRange | undefined; presetId: string | undefined }) => void
  /** Months shown side by side. Defaults to 2 (hotel/flight style). */
  numberOfMonths?: number
  /** Disable selecting days before this date. */
  fromDate?: Date
  /** Disable selecting days after this date. */
  toDate?: Date
  /** Caption navigation style. Defaults to free month + year dropdowns. */
  captionLayout?: "label" | "dropdown" | "dropdown-months" | "dropdown-years"
  /** Earliest navigable month. Defaults to 10 years before today. */
  startMonth?: Date
  /** Latest navigable month. Defaults to `toDate` or the current month. */
  endMonth?: Date
  /** Snap selection to whole months instead of individual days. */
  selectMonth?: boolean
  /** Pick start/end months independently instead of a coupled calendar range. */
  independentMonths?: boolean
  disabled?: boolean
  align?: "start" | "center" | "end"
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  id?: string
  triggerClassName?: string
  labels?: DateRangePickerLabels
}

/**
 * Reveals the trigger's full range on hover with no delay.
 *
 * Carries its own provider so the primitive stays self-contained, and steps out
 * of the way entirely when there is no text or the popover is open.
 */
function TriggerTooltip({
  text,
  suppressed,
  children,
}: {
  text: string | undefined
  suppressed: boolean
  children: React.ReactNode
}) {
  if (!text || suppressed) return <>{children}</>
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="bottom">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function DateRangePicker({
  value,
  onChange,
  presets,
  activePresetId,
  onPresetSelect,
  onCommit,
  numberOfMonths = 2,
  fromDate,
  toDate,
  captionLayout = "dropdown",
  startMonth,
  endMonth,
  selectMonth = false,
  independentMonths = true,
  disabled,
  align = "start",
  weekStartsOn,
  id,
  triggerClassName,
  labels,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [monthPanelValid, setMonthPanelValid] = React.useState(true)
  const isNarrowViewport = useIsNarrowViewport()
  const hasPresets = Boolean(presets && presets.length > 0)
  // Rail mode defers every change to Apply; the month-range consumer keeps its
  // original live behaviour.
  const isDeferred = hasPresets && Boolean(onCommit)
  const [draftRange, setDraftRange] = React.useState<DateRange | undefined>(value)
  const [draftPresetId, setDraftPresetId] = React.useState<string | undefined>(activePresetId)

  // Reseed the draft from the committed value whenever the popover opens or
  // closes, so an unapplied edit is discarded rather than remembered.
  React.useEffect(() => {
    setDraftRange(value)
    setDraftPresetId(activePresetId)
  }, [open, value, activePresetId])

  // The draft drives the popover's own contents only. The trigger always reports
  // committed state, so closing without applying leaves the button unchanged.
  const shownRange = isDeferred ? draftRange : value
  const shownPresetId = isDeferred ? draftPresetId : activePresetId
  const triggerRange = value
  const triggerPresetId = activePresetId

  const disabledMatcher = React.useMemo<Matcher[] | undefined>(() => {
    const matchers: Matcher[] = []
    if (fromDate) matchers.push({ before: fromDate })
    if (toDate) matchers.push({ after: toDate })
    return matchers.length > 0 ? matchers : undefined
  }, [fromDate, toDate])

  const resolvedStartMonth = React.useMemo(() => {
    if (startMonth) return startMonth
    if (fromDate) return fromDate
    const today = new Date()
    return new Date(today.getFullYear() - 10, 0, 1)
  }, [startMonth, fromDate])

  const resolvedEndMonth = React.useMemo(() => {
    if (endMonth) return endMonth
    if (toDate) return toDate
    return new Date()
  }, [endMonth, toDate])

  const nights = countNights(shownRange)
  const months = countMonths(shownRange)
  const hasRange = Boolean(shownRange?.from)
  const hasCompleteRange = Boolean(shownRange?.from && shownRange.to)
  // Apply stays disabled until both ends exist, so a half-finished range cannot
  // be committed.
  const canConfirm = isDeferred
    ? hasCompleteRange
    : selectMonth && independentMonths
      ? monthPanelValid && hasCompleteRange
      : true
  const rangeCount = selectMonth ? months : nights
  const rangeSummary =
    selectMonth && hasCompleteRange && rangeCount > 0 && labels?.months
      ? labels.months(rangeCount)
      : !selectMonth && rangeCount > 0 && labels?.nights
        ? labels.nights(rangeCount)
        : ""

  const activePreset = presets?.find((preset) => preset.id === shownPresetId)
  // A range that matches no supplied preset is the custom state. Keying on the
  // range rather than the preset id keeps the consumer's sentinel out of the
  // design system *and* marks Custom the moment a day is clicked — the draft
  // preset id is cleared by that click, so it cannot carry this signal.
  const isCustomActive = hasPresets && Boolean(shownRange?.from) && !activePreset

  const triggerLabel = (() => {
    if (selectMonth) {
      return formatMonthRangeLabel(triggerRange) ?? labels?.placeholder ?? "Select months"
    }
    if (triggerRange?.from && triggerRange.to) {
      return `${formatDay(triggerRange.from)} – ${formatDay(triggerRange.to)}`
    }
    if (triggerRange?.from) return `${formatDay(triggerRange.from)} – …`
    return labels?.placeholder ?? "Select dates"
  })()

  // What Apply would commit right now. Distinct from the trigger's label, which
  // reports committed state so an abandoned draft cannot leak onto the button.
  const draftRangeLabel = (() => {
    if (shownRange?.from && shownRange.to) {
      return `${formatDay(shownRange.from)} – ${formatDay(shownRange.to)}`
    }
    if (shownRange?.from) return `${formatDay(shownRange.from)} – …`
    return ""
  })()

  const triggerPreset = presets?.find((preset) => preset.id === triggerPresetId)
  // The trigger names the preset and exposes the dates on hover; a committed
  // custom range has no name, so it shows the dates directly.
  const triggerText = triggerPreset ? triggerPreset.label : triggerLabel
  const triggerHoverText =
    triggerPreset && triggerRange?.from ? triggerLabel : undefined

  const [displayedMonth, setDisplayedMonth] = React.useState<Date | undefined>(
    () => value?.from ?? resolvedEndMonth,
  )
  const selectionStartTime = shownRange?.from?.getTime()

  // A preset can resolve far outside the visible months — a season bracket lands
  // in a prior year — so the view follows the selection instead of staying put.
  // While closed the displayed month is unobservable, so only track it when open.
  React.useEffect(() => {
    if (open && selectionStartTime !== undefined) {
      setDisplayedMonth(new Date(selectionStartTime))
    }
  }, [open, selectionStartTime])

  function handleSelect(range: DateRange | undefined) {
    const next = selectMonth ? snapRangeToMonths(range) : range
    if (isDeferred) {
      setDraftRange(next)
      // A calendar edit is no longer any preset; the consumer's sentinel arrives
      // back through onCommit as `undefined`.
      setDraftPresetId(undefined)
      return
    }
    onChange(next)
  }

  function handlePresetSelect(presetId: string) {
    const preset = presets?.find((entry) => entry.id === presetId)
    if (isDeferred) {
      setDraftPresetId(presetId)
      if (preset) setDraftRange(preset.range)
      return
    }
    onPresetSelect?.(presetId)
  }

  function handleApply() {
    onCommit?.({ range: draftRange, presetId: draftPresetId })
    setOpen(false)
  }

  function handleClear() {
    if (isDeferred) {
      // Empties the calendar. Apply stays disabled until a range is picked again.
      setDraftRange(undefined)
      setDraftPresetId(undefined)
      return
    }
    onChange(undefined)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TriggerTooltip text={triggerHoverText} suppressed={open}>
        <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-label={labels?.triggerAria ?? labels?.placeholder ?? "Select date range"}
          data-empty={!triggerRange?.from}
          className={cn(
            "flex h-9 items-center gap-2 rounded-sm border border-input-border bg-input-background px-3 text-left text-sm outline-none transition-colors",
            // Content-driven so a full custom range is never clipped. The
            // month-range consumer restores w-full through triggerClassName.
            "w-auto",
            "hover:border-input-focus-border focus-visible:border-input-focus-border",
            "disabled:cursor-not-allowed disabled:opacity-40",
            triggerClassName,
          )}
        >
          <Icon name="calendar" size="sm" color="muted" className="shrink-0" />
          <span
            className={cn(
              "flex-1 whitespace-nowrap",
              !triggerRange?.from && "text-muted-foreground",
            )}
          >
            {triggerText}
          </span>
          {rangeSummary ? (
            <span className="shrink-0 text-xs font-medium text-muted-foreground">
              {rangeSummary}
            </span>
          ) : null}
        </button>
        </PopoverTrigger>
      </TriggerTooltip>
      <PopoverContent
        align={align}
        collisionPadding={8}
        className={cn(
          "w-auto bg-muted p-0",
          // Cap to remaining viewport so Clear/Apply cannot sit below the fold
          // on a 1280×800 dashboard. The rail scrolls; the footer stays put.
          hasPresets &&
            "flex max-h-[var(--radix-popover-content-available-height)] flex-col overflow-hidden",
        )}
        sideOffset={4}
      >
        <div
          className={cn(
            hasPresets && "relative min-h-0 flex-1 max-w-[95vw]",
            hasPresets && PRESET_RAIL_OFFSET_CLASS,
          )}
        >
        {hasPresets && presets ? (
          <DateRangePresetRail
            className={PRESET_RAIL_LAYOUT_CLASS}
            presets={presets}
            activePresetId={shownPresetId}
            onPresetSelect={handlePresetSelect}
            customLabel={labels?.custom}
            isCustomActive={isCustomActive}
            listAriaLabel={labels?.presetList}
          />
        ) : null}
        {selectMonth && independentMonths ? (
          <MonthRangePickerPanel
            value={value}
            onChange={onChange}
            onValidityChange={setMonthPanelValid}
            startMonth={resolvedStartMonth}
            endMonth={resolvedEndMonth}
            fromDate={fromDate}
            toDate={toDate}
            labels={{
              from: labels?.from,
              to: labels?.to,
              month: labels?.month,
              year: labels?.year,
              invalidRange: labels?.invalidRange,
              previousYear: labels?.previousYear,
              nextYear: labels?.nextYear,
            }}
          />
        ) : (
          <Calendar
            mode="range"
            // With a rail present, focus belongs on the active preset instead.
            autoFocus={!hasPresets}
            captionLayout={captionLayout}
            {...(hasPresets ? { month: displayedMonth, onMonthChange: setDisplayedMonth } : { defaultMonth: shownRange?.from ?? resolvedEndMonth })}
            startMonth={resolvedStartMonth}
            endMonth={resolvedEndMonth}
            selected={shownRange}
            onSelect={handleSelect}
            numberOfMonths={hasPresets && isNarrowViewport ? 1 : numberOfMonths}
            weekStartsOn={weekStartsOn}
            disabled={disabledMatcher}
          />
        )}
        </div>
        <Separator className="shrink-0" />
        <div className="flex shrink-0 items-center justify-between gap-3 p-2.5">
          <span className="pl-1 text-xs text-muted-foreground">
            {isDeferred ? (draftRangeLabel || labels?.placeholder) : rangeSummary}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={!hasRange}
            >
              {labels?.clear ?? "Clear"}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!canConfirm}
              onClick={isDeferred ? handleApply : () => setOpen(false)}
            >
              {isDeferred ? (labels?.apply ?? "Apply") : (labels?.done ?? "Done")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { DateRangePicker }
